import React, { useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TablePagination, Stack, TextField, Box, IconButton, Tooltip, Card, MenuItem, Select, FormControl, InputLabel, SelectChangeEvent } from '@mui/material';
import InputAdornment from '@mui/material/InputAdornment';
import Iconify from 'src/components/iconify';
import { format, addDays, subDays, startOfDay, isToday } from 'date-fns';

// ----------------------------------------------------------------------

// Generate date options for dropdown (previous 30 days, today, and next 2 days)
export const generateDateOptions = () => {
  const today = startOfDay(new Date());
  const options = [];

  // Add individual days - previous 30 days
  for (let i = 30; i >= 0; i -= 1) {
    const date = subDays(today, i);
    const isCurrentDay = i === 0;
    options.push({
      label: `${format(date, 'EEE')}, ${format(date, 'dd')}${isCurrentDay ? ' (Today)' : ''}`,
      value: format(date, 'yyyy-MM-dd'),
      isToday: isCurrentDay
    });
  }

  // Add next 2 days
  for (let i = 1; i <= 2; i += 1) {
    const date = addDays(today, i);
    options.push({
      label: `${format(date, 'EEE')}, ${format(date, 'dd')}`,
      value: format(date, 'yyyy-MM-dd'),
      isToday: false
    });
  }

  return options;
};

// ----------------------------------------------------------------------

export interface ITableFilters {
  search: string;
  selectedDay: string | null;
  planTag: string | null;
}

export type ITableFilterValue = string | null;

// ----------------------------------------------------------------------

export function useFilters(tableId = 'default') {
  const [searchParams, setSearchParams] = useSearchParams();

  // Get default selectedDay if not in URL (day after tomorrow)
  const getDefaultSelectedDay = () => {
    const today = new Date();
    return format(addDays(today, 2), 'yyyy-MM-dd'); // Default to the last day (day after tomorrow)
  };

  // Memoize filters to prevent unnecessary re-renders
  const filters = React.useMemo(() => {
    const selectedDay = searchParams.get('selectedDay') || getDefaultSelectedDay();

    return {
      search: searchParams.get('search') || '',
      selectedDay,
      planTag: searchParams.get('planTag') || null
    };
  }, [searchParams]);

  const onFilters = useCallback((name: string, value: ITableFilterValue) => {
    const params = new URLSearchParams(searchParams);
    if (value) {
      params.set(name, value);
    } else {
      params.delete(name);
    }
    params.delete('page'); // Reset page when filters change
    setSearchParams(params);
  }, [searchParams, setSearchParams]);

  const onMultipleFilters = useCallback((filterUpdates: Record<string, ITableFilterValue>) => {
    const params = new URLSearchParams(searchParams);

    Object.entries(filterUpdates).forEach(([name, value]) => {
      if (value) {
        params.set(name, value);
      } else {
        params.delete(name);
      }
    });

    params.delete('page'); // Reset page when filters change
    setSearchParams(params);
  }, [searchParams, setSearchParams]);

  const resetFilters = useCallback(() => {
    setSearchParams(new URLSearchParams());
  }, [setSearchParams]);

  // Calculate date range from selectedDay for API calls, but don't store in URL/session
  const getDateRange = (selectedDay: string | null) => {
    if (!selectedDay) {
      return { date_gte: undefined, date_lte: undefined };
    }

    return {
      date_gte: selectedDay,
      date_lte: selectedDay
    };
  };

  return {
    filters,
    onFilters,
    onMultipleFilters,
    resetFilters,
    getDateRange
  };
}

export function usePagination() {
  const [searchParams, setSearchParams] = useSearchParams();

  const page = Number(searchParams.get('page')) || 0;
  const rowsPerPage = Number(searchParams.get('rowsPerPage')) || 25;

  const onPageChange = useCallback((event: unknown, newPage: number) => {
    const params = new URLSearchParams(searchParams);
    params.set('page', String(newPage));
    setSearchParams(params);
  }, [searchParams, setSearchParams]);

  const onRowsPerPageChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const params = new URLSearchParams(searchParams);
    params.set('rowsPerPage', String(parseInt(event.target.value, 10)));
    params.delete('page'); // Reset to first page when changing rows per page
    setSearchParams(params);
  }, [searchParams, setSearchParams]);

  return { page, rowsPerPage, onPageChange, onRowsPerPageChange };
}

// ----------------------------------------------------------------------

type TableToolbarProps = {
  filters: ITableFilters;
  onFilters: (name: string, value: ITableFilterValue) => void;
  onResetFilters: () => void;
  onMultipleFilters: (updates: Record<string, ITableFilterValue>) => void;
  searchPlaceholder: string;
  planTags?: string[];
  onSearch?: (value: string) => void;
};

export function TableToolbar({
  filters,
  onFilters,
  onResetFilters,
  onMultipleFilters,
  searchPlaceholder,
  planTags = [],
  onSearch
}: TableToolbarProps) {
  const dateOptions = React.useMemo(() => generateDateOptions(), []);

  const handleFilterSearch = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const value = event.target.value;
      if (onSearch) {
        onSearch(value);
      } else {
        onFilters('search', value);
      }
    },
    [onFilters, onSearch]
  );

  const handleDateChange = useCallback(
    (event: SelectChangeEvent) => {
      const value = event.target.value;
      onFilters('selectedDay', value);
    },
    [onFilters]
  );

  const handlePlanTagChange = useCallback(
    (event: SelectChangeEvent) => {
      const value = event.target.value;
      onFilters('planTag', value === 'all' ? null : value);
    },
    [onFilters]
  );

  return (
    <Stack
      spacing={2}
      alignItems={{ xs: 'flex-end', md: 'center' }}
      direction={{
        xs: 'column',
        md: 'row',
      }}
      sx={{
        p: 2.5,
        pr: { xs: 2.5, md: 1 },
      }}
    >
      <Stack direction="row" alignItems="center" spacing={2} flexGrow={1} sx={{ width: 1 }}>
        <TextField
          fullWidth
          value={filters.search}
          onChange={handleFilterSearch}
          placeholder={searchPlaceholder}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Iconify icon="eva:search-fill" sx={{ color: 'text.disabled' }} />
              </InputAdornment>
            ),
          }}
        />

        <FormControl sx={{ minWidth: 220 }}>
          <InputLabel id="date-select-label">Date</InputLabel>
          <Select
            labelId="date-select-label"
            value={filters.selectedDay || ''}
            onChange={handleDateChange}
            label="Date"
            MenuProps={{
              PaperProps: {
                sx: { maxHeight: 400 },
              },
            }}
          >
            {dateOptions.map((option) => (
              <MenuItem
                key={option.value}
                value={option.value}
                sx={option.isToday ? {
                  fontWeight: 'bold',
                  color: 'primary.main',
                  backgroundColor: 'rgba(0, 171, 85, 0.08)',
                  '&:hover': {
                    backgroundColor: 'rgba(0, 171, 85, 0.15)',
                  }
                } : {}}
              >
                {option.isToday ? (
                  <Box component="span" sx={{ display: 'flex', alignItems: 'center' }}>
                    <Iconify icon="mdi:calendar-today" sx={{ mr: 1, color: 'primary.main' }} />
                    {option.label}
                  </Box>
                ) : (
                  option.label
                )}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <Tooltip title="Reset filters">
          <IconButton onClick={onResetFilters}>
            <Iconify icon="mdi:filter-off-outline" />
          </IconButton>
        </Tooltip>
      </Stack>
    </Stack>
  );
}

// ----------------------------------------------------------------------

export interface Column {
  id: string;
  label: string;
  align?: 'left' | 'right' | 'center';
  minWidth?: number;
  format?: (value: any) => string;
  headSx?: Record<string, any>;
}

export interface DataTableProps<T> {
  data: T[] | undefined;
  columns: Column[];
  loading: boolean;
  error: any;
  page: number;
  rowsPerPage: number;
  count: number;
  onPageChange: (event: unknown, newPage: number) => void;
  onRowsPerPageChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  renderRow: (item: T, index: number) => React.ReactNode;
  emptyText?: string;
  loadingText?: string;
  errorText?: string;
}

export function DataTable<T>({
  data,
  columns,
  loading,
  error,
  page,
  rowsPerPage,
  count,
  onPageChange,
  onRowsPerPageChange,
  renderRow,
  emptyText = 'No data found',
  loadingText = 'Loading...',
  errorText = 'Error loading data',
}: DataTableProps<T>) {
  const renderContent = () => {
    if (loading) {
      return (
        <TableRow>
          <TableCell colSpan={columns.length} align="center">
            {loadingText}
          </TableCell>
        </TableRow>
      );
    }

    if (error) {
      return (
        <TableRow>
          <TableCell colSpan={columns.length} align="center">
            {errorText}
          </TableCell>
        </TableRow>
      );
    }

    if (!data?.length) {
      return (
        <TableRow>
          <TableCell colSpan={columns.length} align="center">
            {emptyText}
          </TableCell>
        </TableRow>
      );
    }

    return data.map((item, index) => renderRow(item, index));
  };

  return (
    <>
      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              {columns.map((column) => (
                <TableCell
                  key={column.id}
                  align={column.align}
                  style={{ minWidth: column.minWidth }}
                  sx={column.headSx}
                >
                  {column.label}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {renderContent()}
          </TableBody>
        </Table>
      </TableContainer>

      <TablePagination
        rowsPerPageOptions={[5, 10, 25]}
        component="div"
        count={count}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={onPageChange}
        onRowsPerPageChange={onRowsPerPageChange}
      />
    </>
  );
}
